import React, { useCallback, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { HostedFrame, type HostedFrameRef, theme, useResponsive } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';
import { getEnvVars } from '@order/utils';

import { useSetupIntent } from '../../../hooks';
import { useCreditCardForm, useStripeFormSubmission, useStripeMessages } from '../hooks';
import { FRAME_HEIGHT, getStripeSource, STRIPE_FRAME_ID } from '../utils';
import { CreditCardFormControlledContent } from './CreditCardFormControlledContent';
import { CreditCardFormErrorState } from './CreditCardFormErrorState';
import { CreditCardFormLoadingState } from './CreditCardFormLoadingState';

export const CreditCardFormAdd = (props: CreditCardFormAddProps) => {
  const { excludedFields, isSubmitting, handleOnSave } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const hostedFrameRef = useRef<HostedFrameRef>(null);
  const { match } = useResponsive();
  const { track } = useTelemetry();
  const { formatMessage } = useIntl();

  // ─── Controlled Form State ───────────────────────────────────────────

  const { formData, updateForm } = useCreditCardForm({ default: true });

  // ─── Submission Results ──────────────────────────────────────────────

  const handleSuccess = useCallback(
    (id: string) => {
      track('payment_credit.save_card');

      handleOnSave({
        id,
        nickname: formData.nickname,
        isDefault: formData.default,
      });
    },
    [formData.default, formData.nickname, track, handleOnSave],
  );

  // ─── Styles ──────────────────────────────────────────────────────────

  const webViewBackgroundColor = MAIN_BACKGROUND_COLOR;
  const containerBackgroundColor = styles.container;

  // ─── Stripe Initialization ───────────────────────────────────────────

  const { handleSubmitStripeForm, handleStripeError } = useStripeFormSubmission({ hostedFrameRef });

  const { setupIntentSecret, isFetchingSetupIntent } = useSetupIntent();

  const postalCodeErrorMessage = formatMessage(messages.creditCardFormErrorBannerZipCode);
  const stripeWebSource = getStripeSource({
    stripeKey: stripePublicKey,
    clientSecret: setupIntentSecret,
    postalCodeErrorMessage,
    backgroundColor: webViewBackgroundColor,
  });

  const { isSubmittingStripe, canSubmitForm, handleStripeMessages } = useStripeMessages({
    onSuccess: handleSuccess,
    onError: handleStripeError,
  });

  // ─── Rendering Control ───────────────────────────────────────────────

  if (!setupIntentSecret && !isFetchingSetupIntent) {
    return <CreditCardFormErrorState />;
  }

  return (
    <CreditCardFormControlledContent
      mode="add"
      canSubmitForm={canSubmitForm}
      isSaving={isSubmittingStripe || isSubmitting}
      form={formData}
      excludedFields={excludedFields}
      onFormChange={updateForm}
      onSubmitForm={handleSubmitStripeForm}
    >
      <View style={[containerBackgroundColor, styles.wrapper]}>
        <CreditCardFormLoadingState
          style={[containerBackgroundColor, styles.wrapper]}
          show={isFetchingSetupIntent}
        />

        <HostedFrame
          ref={hostedFrameRef}
          frameId={STRIPE_FRAME_ID}
          frameHeight={FRAME_HEIGHT}
          title={formatMessage(messages.creditCardFormTitle)}
          isLoading={isFetchingSetupIntent}
          source={stripeWebSource}
          onMessage={handleStripeMessages}
          style={containerBackgroundColor}
          containerStyle={[
            containerBackgroundColor,
            styles.wrapper,
            match([styles.containerXS, styles.containerSM]),
            isFetchingSetupIntent && styles.loadingFields,
          ]}
        />
      </View>
    </CreditCardFormControlledContent>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  creditCardFormErrorBannerZipCode: {
    defaultMessage: `Your card's zip code is invalid.`,
    description: 'Credit Card Form | Error | Banner | ZipCode',
  },
  creditCardFormTitle: {
    defaultMessage: 'Credit card form',
    description: 'Credit Card Form | Title',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const MAIN_BACKGROUND_COLOR = theme.colors.APP_BACKGROUND;
const stripePublicKey = getEnvVars().STRIPE_PUBLIC_KEY;

// ─── Types ───────────────────────────────────────────────────────────────────

type CreditCardFormAddProps = Readonly<{
  excludedFields?: ReadonlyArray<'nickname' | 'isDefault'>;
  isSubmitting: boolean;
  handleOnSave: (payload: CreditCardForm) => void;
}>;

type CreditCardForm = Readonly<{
  id: string;
  nickname: string;
  isDefault: boolean;
}>;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    height: FRAME_HEIGHT,
  },
  container: {
    backgroundColor: MAIN_BACKGROUND_COLOR,
  },
  containerXS: {
    paddingHorizontal: theme.spacing['4'],
  },
  containerSM: {
    paddingHorizontal: theme.spacing['6'],
  },
  // for web views to function properly on Android, their container must be at least 1x1 in size
  loadingFields: {
    flex: 0,
    width: 1,
    height: 1,
  },
});
